import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { stockApi } from '../../api/stock-api';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { SearchableSelect } from '../../components/SearchableSelect';
import { requirementTypeLabels, type FulfillmentRequirement } from '../../domain/fulfillment';
import { AddLinePicker, LineItemPicker } from './LineItemPicker';
import { STOCK_QUERY_KEYS } from '../stock/use-stock-references';
import type { LineChoice } from './line-picker-entries';
import {
  generationOfChoice,
  generationsOfType,
  partChoiceOf,
  partGenerationLabels,
  partIdsForChoice,
  partIdsOfGeneration,
  partIdsOfType,
  type PartGeneration,
} from './part-choice';
import {
  lineChoiceOf,
  newRequirementRow,
  requirementForChoice,
  type RequirementRow,
} from './requirement-rows';
import { locationShortfall, pickFromOptions } from './stock-locations';
import type { FulfillmentReferences } from './use-fulfillment-references';

/**
 * Edits a list of serialized and stock requirements. Each line is for
 * whatever is chosen from one list of part types and stock items; its type
 * follows from that. Orders also choose where each stock line is picked
 * from; left empty, release picks the location that can cover the whole
 * quantity. The choices are the locations that have the item, with what is
 * available at each.
 */
export function RequirementsEditor({
  rows,
  onChange,
  references,
  showLocations = false,
  single = false,
  label,
}: {
  rows: RequirementRow[];
  onChange: (rows: RequirementRow[]) => void;
  references: FulfillmentReferences;
  showLocations?: boolean;
  /** Exactly one row: no add or remove buttons. */
  single?: boolean;
  label: string;
}) {
  // Lines someone switched to picking builds by hand.
  const [byBuild, setByBuild] = useState<ReadonlySet<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const partOptions = references.parts.map((part) => ({
    value: part.id,
    label: references.partName(part.id),
  }));
  function buildOptions(type: string) {
    return partIdsOfType(references.parts, type).map((id) => {
      const part = references.parts.find((candidate) => candidate.id === id);
      return { value: id, label: part ? `${part.build} (${id})` : id };
    });
  }
  const activeStockItems = references.stockItems.filter((item) => item.active);
  const balances = useQuery({
    queryKey: STOCK_QUERY_KEYS.balances,
    queryFn: () => stockApi.listStockBalances(),
    enabled: showLocations,
  });

  function update(key: string, change: (row: RequirementRow) => RequirementRow): void {
    onChange(rows.map((row) => (row.key === key ? change(row) : row)));
  }

  function updateRequirement(key: string, change: Partial<FulfillmentRequirement>): void {
    update(key, (row) => ({ ...row, requirement: { ...row.requirement, ...change } }));
  }

  function choose(key: string, choice: LineChoice): void {
    setByBuild((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    update(key, (row) => ({
      ...row,
      requirement: requirementForChoice(row.requirement, choice, references.parts),
      // Only stock lines are picked from a location.
      locationId: choice.kind === 'stock' ? row.locationId : undefined,
    }));
  }

  return (
    <fieldset className="requirements-editor">
      <legend>{label}</legend>
      {rows.length === 0 ? <p className="empty-note">No lines yet.</p> : null}
      <ol className="requirement-rows">
        {rows.map((row, index) => {
          const r = row.requirement;
          const lineName = `Line ${index + 1}`;
          return (
            <li key={row.key} className="requirement-row">
              <div className="requirement-row-heading">
                <strong>
                  {lineName}: {requirementTypeLabels[r.type]}
                </strong>
                {single ? null : (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => onChange(rows.filter((other) => other.key !== row.key))}
                    aria-label={`Remove ${lineName}`}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="requirement-row-fields">
                {/* A div, not a label: a click on the caption would open the list. */}
                <div className="stock-picker-field">
                  <span className="stock-picker-caption">Item</span>
                  <LineItemPicker
                    ariaLabel={`${lineName} item`}
                    value={lineChoiceOf(r, references.parts)}
                    stockItems={activeStockItems}
                    parts={references.parts}
                    onChange={(choice) => choose(row.key, choice)}
                  />
                </div>
                <label>
                  Label
                  <input
                    value={r.label ?? ''}
                    onChange={(event) => updateRequirement(row.key, { label: event.target.value })}
                    placeholder={r.type === 'SERIALIZED' ? 'e.g. Camera' : 'e.g. M12 plugs'}
                    aria-label={`${lineName} label`}
                  />
                </label>
                {r.type === 'SERIALIZED'
                  ? (() => {
                      const choice = partChoiceOf(r.partIds, references.parts);
                      // The multi-select pickers sit in a div, not a label:
                      // WebKit re-clicks a label's button after Apply, which
                      // reopens the picker.
                      if (choice.mixed) {
                        return (
                          <div className="picker-field">
                            Acceptable parts
                            <MultiSelectFilter
                              ariaLabel={`${lineName} parts`}
                              values={r.partIds}
                              onChange={(partIds) => updateRequirement(row.key, { partIds })}
                              options={partOptions}
                              placeholder="Choose parts"
                            />
                          </div>
                        );
                      }
                      const generations = generationsOfType(references.parts, choice.type);
                      const generation = generationOfChoice(references.parts, choice);
                      // Gen 1 / Gen 2 for camera and winch systems; builds only
                      // when asked for, or when a line already names some.
                      const showGeneration = generations.length > 1;
                      const generationValue =
                        byBuild.has(row.key) || (choice.partIds.length > 0 && !generation)
                          ? 'builds'
                          : (generation ?? '');
                      const showBuilds =
                        partIdsOfType(references.parts, choice.type).length > 1 &&
                        (!showGeneration || generationValue === 'builds');
                      return (
                        <>
                          {showGeneration ? (
                            <label>
                              Generation
                              <select
                                aria-label={`${lineName} generation`}
                                value={generationValue}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setByBuild((current) => {
                                    const next = new Set(current);
                                    if (value === 'builds') next.add(row.key);
                                    else next.delete(row.key);
                                    return next;
                                  });
                                  if (value === 'builds') return;
                                  updateRequirement(row.key, {
                                    partIds: value
                                      ? partIdsOfGeneration(
                                          references.parts,
                                          choice.type,
                                          value as PartGeneration,
                                        )
                                      : partIdsForChoice(references.parts, choice.type, []),
                                  });
                                }}
                              >
                                <option value="">Any</option>
                                {generations.map((option) => (
                                  <option key={option} value={option}>
                                    {partGenerationLabels[option]}
                                  </option>
                                ))}
                                <option value="builds">Specific builds</option>
                              </select>
                            </label>
                          ) : null}
                          {showBuilds ? (
                            <div className="picker-field">
                              Builds
                              <MultiSelectFilter
                                ariaLabel={`${lineName} builds`}
                                values={choice.partIds}
                                onChange={(restrictTo) =>
                                  updateRequirement(row.key, {
                                    partIds: partIdsForChoice(
                                      references.parts,
                                      choice.type,
                                      restrictTo,
                                    ),
                                  })
                                }
                                options={buildOptions(choice.type)}
                                placeholder="Any build"
                              />
                            </div>
                          ) : null}
                        </>
                      );
                    })()
                  : null}
                <label className="quantity-field">
                  Quantity
                  {r.type === 'STOCK' && r.stockItemId
                    ? ` (${references.unit(r.stockItemId)})`
                    : ''}
                  <input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={Number.isFinite(r.quantity) ? r.quantity : ''}
                    onChange={(event) =>
                      updateRequirement(row.key, {
                        quantity: Number.parseInt(event.target.value, 10),
                      })
                    }
                    aria-label={`${lineName} quantity`}
                  />
                </label>
                {showLocations && r.type === 'STOCK' ? (
                  <label>
                    Pick from
                    <SearchableSelect
                      ariaLabel={`${lineName} location`}
                      value={row.locationId ?? ''}
                      onChange={(locationId) =>
                        update(row.key, (current) => ({
                          ...current,
                          locationId: locationId || undefined,
                        }))
                      }
                      options={pickFromOptions({
                        locations: references.locations,
                        balances: balances.data,
                        stockItemId: r.stockItemId,
                        current: row.locationId,
                        locationName: references.locationName,
                      })}
                      placeholder="Choose at release"
                    />
                    {(() => {
                      const shortfall = locationShortfall({
                        locations: references.locations,
                        balances: balances.data,
                        stockItemId: r.stockItemId,
                        locationId: row.locationId,
                        quantity: r.quantity,
                        locationName: references.locationName,
                      });
                      return shortfall ? (
                        <span className="field-hint location-shortfall" role="status">
                          {shortfall.text}. Release takes only from this location, so the line would
                          wait for stock.
                        </span>
                      ) : null;
                    })()}
                  </label>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="requirement-actions" hidden={single}>
        <button type="button" onClick={() => setAdding(true)}>
          Add line
        </button>
      </div>
      {adding ? (
        <AddLinePicker
          title={`Line ${rows.length + 1} item`}
          stockItems={activeStockItems}
          parts={references.parts}
          onChoose={(choice) => {
            setAdding(false);
            const row = newRequirementRow('STOCK');
            onChange([
              ...rows,
              {
                ...row,
                requirement: requirementForChoice(row.requirement, choice, references.parts),
              },
            ]);
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </fieldset>
  );
}
